// Batch 43 (T41) — B13 tokens for the whole application.
//
// This file was born as `Pages/ProgramLibrary/shared/abaTokens.ts` because
// the ABA module led the Batch 13 visual-system revision. Once every
// module wanted the same palette, the "ABA" naming became a lie about
// scope — Finance, Parents, Classes, and Settings all read from here.
// Hoisted to `theme/` and prefix-dropped in Batch 43. Behaviour identical
// to the old file; only names and location changed.
//
// EVERY colour, radius, spacing, or type decision on a redesigned page
// must be derived from this file. Components must not spell hex values
// inline; if you need a new one, add it here first and reference it.
// Search the codebase for a raw # to catch drift.
//
// Two palettes live side-by-side:
//   - LegacyTokens (Batch 37, warm green-grey) — pages that haven't been
//     rewritten yet. Kept for parity, in-migration to B13.
//   - B13 (Batch 13 Part B, cool near-neutral) — the going-forward palette
//     for every new or redesigned surface.
//
// The `Aba*`-prefixed exports at the old path are backward-compat aliases
// (see the shim at Pages/ProgramLibrary/shared/abaTokens.ts). Delete the
// shim once no consumer imports from the old path — see T43.

export const LegacyTokens = {
  // §1.1 Surface + text
  ink:      "#10231C",  // page title, primary values
  ink2:     "#3D4F48",  // body text
  // Batch 37 (a) — darkened from the spec's #6B7C76 to clear AA (4.5:1) on
  // both surface (5.32:1) and canvas2 (4.71:1). The original hex failed by
  // 0.07 on white, 0.59 on canvas2 — with labels sitting on canvas2 in
  // table headers, that would leave every label below the small-text bar.
  ink3:     "#5A6C66",  // labels, captions, secondary
  line:     "#E3E9E6",  // hairlines, dividers, table borders
  surface:  "#FFFFFF",  // cards
  canvas:   "#F4F7F5",  // page background — already the module's warm green-grey
  canvas2:  "#EDF2EF",  // subtle inset surfaces, table headers

  // §1.2 Brand
  brand:      "#0F3D2E",  // deep green — primary actions, active tab indicator
  brandSoft:  "#E6EFEA",  // brand at low weight — active tab fill, selection
  brandLine:  "#0F3D2E",  // active indicators

  // Batch 39 — supplementary accents for KPI cards and folder-depth icons.
  // Values chosen to sit inside the existing status-triad range so nothing
  // reads as a new hue on the page. Every use of these must be a tinted
  // background (X + Xsoft) or a small dot — never a full-saturation surface.
  teal:       "#0E8C8C", tealSoft:   "#E1F1F0",   tealText:    "#0B6B6B",  // Active KPI, child-folder icon
  brandTile:  "#E6EFEA",                                                     // Programs KPI icon tile (matches brandSoft)
  blue:       "#2B7CE9", blueSoft:   "#E6EFFB",   blueText:    "#14509E",  // Students KPI, deeper-folder icon
  purple:     "#8161D6", purpleSoft: "#F0ECFA",   purpleText:  "#55389E",  // Active Targets KPI
  emerald:    "#12A05C", emeraldSoft:"#E4F3EA",   emeraldText: "#0B6B3A",  // Mastered Targets KPI
  amber:      "#E0A03A", amberSoft:  "#FDF1E0",   amberText:   "#8A5406",  // Sessions This Week KPI

  // Batch 42 — supplementary hues used by Prompt Hierarchy levels and
  // the Discontinued status. Two more so the Editor's default palette can
  // stop spelling raw hex and align with the List's chip fallback.
  red:        "#D14343", redSoft:    "#FBEAEA",   redText:     "#9E2020",  // Full Physical / Discontinued
  clay:       "#E07A3A", claySoft:   "#FDECDD",   clayText:    "#8A3E06",  // Partial Physical / Returned

  // Batch 42 — neutral triad for archived / voided / inactive states.
  // Extracted from three list pages that were spelling the same three
  // hex values inline (#EFF2F1 / #5A6C66 / #9AA8A3). Same visual weight
  // as the muted section of §1.3 (waiting/closed) but reserved for
  // "shelved" rather than "not yet started".
  neutralSoft: "#EFF2F1", neutralText: "#5A6C66", neutralDot: "#9AA8A3",

  // §1.6 Spacing — 4px base; the only allowed multiples are 8/12/16/24/32/48.
  space: { 8: 8, 12: 12, 16: 16, 24: 24, 32: 32, 48: 48 } as const,

  // §1.7 Radius — one set, no mixing.
  radius: { card: 10, control: 8, chip: 6 } as const,

} as const;

// §1.4 Data-visualisation series palette (in order).
// Defined outside the LegacyTokens literal because a literal cannot
// reference its own siblings during initialisation. Consumers that
// previously read `LegacyTokens.seriesPalette` should read this export.
export const SeriesPalette = [
  LegacyTokens.emerald,
  LegacyTokens.blue,
  LegacyTokens.amber,
  LegacyTokens.purple,
  LegacyTokens.red,
  LegacyTokens.teal,
] as const;

// Batch 40 — the ABA module now sits on a clean white surface instead of
// the app-wide mint canvas. Every page's outer container spreads this sx
// so the mint bleeds out around the ABA route without touching the
// palette-level background.default (which is app-global, non-ABA pages
// keep their mint).
export const PageSurface = {
  // Batch 40 — MiniDrawer's main container is now conditionally white for
  // /aba-therapy/* routes (Menu.tsx line ~1453), so pages no longer need
  // negative-margin bleed. This is kept as a no-op-with-intent so ABA
  // page code still reads as "spread the surface" and so a future
  // per-page override has a hook. If the shell logic ever changes back,
  // reintroduce the bleed here rather than in every page.
  backgroundColor: LegacyTokens.surface,
} as const;

// Batch 40 — the "premium workspace" card shadow. Applied to the major
// containers on white-bg pages (tree card, table card, KPI cards on hover)
// so they don't disappear into the surface. Kept subtle per spec.
export const CardShadow = "0 4px 18px rgba(15, 56, 45, 0.07)";

// §1.3 STATUS — three values per status: chip background, chip text, dot.
// The chip is the tint with dark text; the dot is the only saturated
// element. This is what stops seven statuses from shouting at each other.
export type StatusKey =
  | "mastered" | "acquisition" | "probe" | "waiting"
  | "hold" | "closed" | "discontinued";

type StatusTriad = { bg: string; text: string; dot: string; label: string };

export const StatusColors: Record<StatusKey, StatusTriad> = {
  mastered:     { bg: "#E4F3EA", text: "#0B6B3A", dot: "#12A05C", label: "Mastered" },
  acquisition:  { bg: "#E6EFFB", text: "#14509E", dot: "#2B7CE9", label: "Acquisition" },
  probe:        { bg: "#FDF1E0", text: "#8A5406", dot: "#E0A03A", label: "Probe" },
  waiting:      { bg: "#EFF2F1", text: "#5A6B65", dot: "#9AA8A3", label: "Waiting" },
  hold:         { bg: "#F0ECFA", text: "#55389E", dot: "#8161D6", label: "Hold" },
  closed:       { bg: "#E9ECEB", text: "#4A5651", dot: "#7E8B86", label: "Closed" },
  discontinued: { bg: "#FBEAEA", text: "#9E2020", dot: "#D14343", label: "Discontinued" },
};

// Batch 43 §1 — programs and targets have DIFFERENT status vocabularies
// but share the seven-triad colour set. Program statuses reuse the target
// triads for palette (baseline→probe colours, active→acquisition
// colours, maintenance→hold colours) but keep their own display labels.
// This map is the single source of truth for program-status LABELS —
// consumers must NOT read `.label` off resolveStatusTriad's return, since
// that would render "Acquisition" for an Active program.
export const PROGRAM_STATUS_LABELS: Record<string, string> = {
  waiting:      "Waiting",
  baseline:     "Baseline",
  active:       "Active",
  maintenance:  "Maintenance",
  hold:         "Hold",
  closed:       "Closed",
  discontinued: "Discontinued",
};

// Human-readable label for any program-status raw value. Falls back to
// title-casing the raw string so a future enum addition doesn't render as
// an empty chip.
export function programStatusLabel(raw: string | null | undefined): string {
  const v = String(raw ?? "").toLowerCase();
  if (PROGRAM_STATUS_LABELS[v]) return PROGRAM_STATUS_LABELS[v];
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : "—";
}

// Program statuses map onto the same triads (Batch 37 §1.3):
//   Baseline    → Probe's set
//   Active      → Acquisition's set
//   Maintenance → Hold's set
// Everything else uses its own key.
//
// Batch 43 §1 — the returned triad's `.label` reflects the ALIASED target
// status (e.g. "Acquisition" for Active). Program-status callers must use
// `programStatusLabel(raw)` above for the display label; only bg/text/dot
// are safe to consume from the triad.
export function resolveStatusTriad(raw: string | null | undefined): StatusTriad {
  const v = String(raw ?? "").toLowerCase();
  const programAliases: Record<string, StatusKey> = {
    baseline: "probe",
    active: "acquisition",
    maintenance: "hold",
  };
  const key = (programAliases[v] ?? (v as StatusKey));
  return StatusColors[key] ?? StatusColors.waiting;
}

// §1.5 Type scale — every text element on a Batch-37 surface picks a preset.
// Font-family stays whatever the app theme resolves (system stack).
export const TypeScale = {
  pageTitle:    { fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: LegacyTokens.ink },
  sectionHead:  { fontSize: 15, fontWeight: 650, color: LegacyTokens.ink },
  fieldLabel:   { fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: LegacyTokens.ink3 },
  fieldValue:   { fontSize: 15, fontWeight: 400, color: LegacyTokens.ink2 },
  tableHeader:  { fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: LegacyTokens.ink3 },
  tableCell:    { fontSize: 14, fontWeight: 400, color: LegacyTokens.ink2 },
  caption:      { fontSize: 13, fontWeight: 400, color: LegacyTokens.ink3 },
} as const;

// ─────────────────────────────────────────────────────────────────────
// Batch 13 Part B — visual system revision. Additive; the legacy
// LegacyTokens above is untouched so pages that have not been redesigned
// yet keep rendering exactly as before. Pages that opt in read from
// B13 / B13Status / B13TypeScale.
//
// Organising rule from §B.0: COLOUR MEANS STATE. GREY MEANS NOTHING
// IS HAPPENING HERE. Every semantic-state chip picks its accent from
// B13Status; every neutral element uses a grey from the palette
// tokens. Nothing on a redesigned page should hardcode a hex.
// ─────────────────────────────────────────────────────────────────────

// §B.1 — the six palette tokens. Cool, near-neutral, calibrated for the
// tinted-chip pattern in B13Status (12% bg, full-strength text/icon).
export const B13 = {
  canvas:  "#F7F9FB",  // page background — cooler than legacy canvas
  surface: "#FFFFFF",  // cards, dialogs, popovers
  ink:     "#16233A",  // primary text — deeper and cooler than legacy ink
  primary: "#1F6FEB",  // primary action, active indicator (same hue as `active` state)
  accent:  "#7C4DFF",  // secondary accent — folders when a colour isn't set, section flourishes
  line:    "#E3E9F2",  // hairlines only — this palette avoids card borders
} as const;

// Muted ink shades for body copy and secondary labels. Derived rather
// than palette-level because they aren't semantic — they're just weight
// steps of ink.
export const B13Ink = {
  ink:   B13.ink,   // primary text
  ink2:  "#3B4A66",    // body / secondary
  ink3:  "#64748B",    // muted labels, captions — matches waiting slate
} as const;

// Turn any semantic hex into a 12% tint on white. Same pattern used
// for chip backgrounds so `#1F6FEB` (active) becomes a soft blue tile
// consistent with the rest of the palette without needing a second
// hardcoded hex per state.
export function tint12(hex: string): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return B13.canvas;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // 12% opacity of colour mixed with 88% white
  const mix = (c: number) => Math.round(c * 0.12 + 255 * 0.88);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

// §B — the seven semantic program-status colours. Each entry is the
// full-strength accent; the chip renderer takes the 12% tint for bg
// and this exact value for text + dot + icon. `label` is display copy.
//
// The seven states match PROGRAM_STATUS_LABELS above so the two maps
// can be joined on the raw status key. `waiting` slate reads as
// "nothing happening here" — the ONLY state whose accent is a
// desaturated grey, by design.
export type B13StatusKey =
  | "waiting" | "baseline" | "active" | "maintenance"
  | "hold" | "closed" | "discontinued";

export const B13Status: Record<B13StatusKey, { accent: string; label: string }> = {
  waiting:      { accent: "#64748B", label: "Waiting" },       // slate — nothing yet
  baseline:     { accent: "#F59E0B", label: "Baseline" },      // amber — assessing
  active:       { accent: "#1F6FEB", label: "Active" },        // blue — teaching
  maintenance:  { accent: "#7C4DFF", label: "Maintenance" },   // violet — holding gains
  hold:         { accent: "#F97316", label: "Hold" },          // orange — paused
  closed:       { accent: "#10B981", label: "Closed" },        // green — done
  discontinued: { accent: "#E11D48", label: "Discontinued" },  // rose — abandoned
};

// Resolve any raw status string to its B13 accent + label. Falls back
// to the waiting slate so a future status enum addition never renders
// as a colour that means something else.
export function resolveB13Status(raw: string | null | undefined) {
  const v = String(raw ?? "").toLowerCase() as B13StatusKey;
  return B13Status[v] ?? B13Status.waiting;
}

// §B.2 — type scale. System-stack humanist for display, system-stack
// neutral for body, `tabular-nums` on any numeric variant so 47 rows
// of counts line up. Font families deliberately not loaded (spec
// forbids new font loaders in this batch).
const FONT_DISPLAY = `system-ui, -apple-system, "Segoe UI", Inter, Roboto, sans-serif`;
const FONT_BODY    = `system-ui, -apple-system, "Segoe UI", Inter, Roboto, sans-serif`;

export const B13TypeScale = {
  h1:         { fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: B13.ink,       lineHeight: 1.2 },
  h2:         { fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 650, letterSpacing: "-0.01em", color: B13.ink,       lineHeight: 1.3 },
  h3:         { fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 650,                            color: B13.ink,       lineHeight: 1.35 },
  body:       { fontFamily: FONT_BODY,    fontSize: 14, fontWeight: 400,                            color: B13Ink.ink2,   lineHeight: 1.5 },
  bodyStrong: { fontFamily: FONT_BODY,    fontSize: 14, fontWeight: 600,                            color: B13.ink,       lineHeight: 1.5 },
  caption:    { fontFamily: FONT_BODY,    fontSize: 12, fontWeight: 400,                            color: B13Ink.ink3,   lineHeight: 1.4 },
  label:      { fontFamily: FONT_BODY,    fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: B13Ink.ink3 },
  num:        { fontFamily: FONT_BODY,    fontSize: 14, fontWeight: 500, color: B13.ink,       fontVariantNumeric: "tabular-nums" as const },
  numLarge:   { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 650, color: B13.ink,       fontVariantNumeric: "tabular-nums" as const, lineHeight: 1.1 },
} as const;
